import * as React from "react";

import { cn } from "@/lib/utils";

type TextSize = "sm" | "md" | "lg";
type TextTone = "default" | "muted" | "strong";

const textScale: Record<TextSize, string> = {
  sm: "text-sm leading-relaxed",
  md: "text-base leading-relaxed",
  lg: "text-lg leading-relaxed",
};

const toneClasses: Record<TextTone, string> = {
  default: "text-foreground",
  muted: "text-muted-foreground",
  strong: "text-foreground font-semibold",
};

type PolymorphicProps<T extends React.ElementType> = {
  as?: T;
  size?: TextSize;
  tone?: TextTone;
  className?: string;
  children: React.ReactNode;
} & Omit<React.ComponentPropsWithoutRef<T>, "as" | "children">;

/**
 * Body text primitive with predictable sizing and tone variants.
 */
export function Text<T extends React.ElementType = "p">({
  as,
  size = "md",
  tone = "default",
  className,
  children,
  ...props
}: PolymorphicProps<T>) {
  const Component = (as ?? "p") as React.ElementType;

  return (
    <Component className={cn(textScale[size], toneClasses[tone], className)} {...props}>
      {children}
    </Component>
  );
}

/**
 * Muted/secondary text convenience wrapper.
 */
export function Muted<T extends React.ElementType = "p">({
  as,
  size = "sm",
  className,
  children,
  ...props
}: PolymorphicProps<T>) {
  const Component = (as ?? "p") as React.ElementType;

  return (
    <Component className={cn(textScale[size], toneClasses.muted, className)} {...props}>
      {children}
    </Component>
  );
}

/**
 * Strong text convenience wrapper.
 */
export function Strong({
  className,
  children,
  ...props
}: Omit<React.HTMLAttributes<HTMLElement>, "children"> & {
  children: React.ReactNode;
}) {
  return (
    <strong className={cn("font-semibold text-foreground", className)} {...props}>
      {children}
    </strong>
  );
}
